import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="terms-back-home"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Go Higher
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#10B981] to-[#0e8f66] grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">
              Go <span className="text-[#D4AF37]">Higher</span>
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
          Legal
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-3 leading-tight">
          Terms of Service
        </h1>
        <p className="mt-4 text-white/50 text-sm">
          Last updated: February 1, 2026
        </p>

        <div className="mt-12 space-y-10 text-white/75 text-[15px] leading-relaxed">
          <Section title="1. Agreement to Terms">
            <p>
              These Terms of Service ("Terms") govern your access to and use of
              services provided by Go Higher, Inc. ("Go Higher," "we," "us," or
              "our"), including our website (igohigher.com), client dashboard,
              review-removal services (Google, Reddit, Glassdoor, Airbnb,
              Trustpilot), and monitoring subscriptions (collectively, the
              "Services"). By accessing or
              using the Services, you agree to be bound by these Terms.
            </p>
          </Section>

          <Section title="2. Services We Provide">
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Google Review Removal — $195 per removed review.</strong>{" "}
                We file removal requests through Google's own policy-compliant
                channels for reviews we believe violate Google's content
                guidelines. You are invoiced only after Google confirms removal.
                If a review is not removed, you owe nothing for that review.
              </li>
              <li>
                <strong>Monitoring — $45/month recurring subscription.</strong>{" "}
                24/7 review monitoring, alerts, and monthly reports. Billed
                monthly to the payment method on file. Cancel anytime; access
                continues to the end of the current billing cycle.
              </li>
              <li>
                <strong>
                  Reddit, Glassdoor, Airbnb & Trustpilot Review Removal —
                  contact for quote.
                </strong>{" "}
                Ethical, policy-mapped removal for content that violates the
                target platform's public content guidelines. Fees quoted per
                case and invoiced only after a review is successfully removed.
              </li>
              <li>
                <strong>Free Audit — no charge, no obligation.</strong>
              </li>
            </ul>
          </Section>

          <Section title="3. What We Do Not Do">
            <p>
              Go Higher is an ethical reputation-management agency. We do not,
              and will not, purchase, generate, incentivize, or otherwise
              manipulate reviews. We do not remove legitimate negative reviews
              from actual customers. We only pursue removals for content that
              violates the target platform's publicly-published policies (spam,
              fake reviews, off-topic, hate speech, conflicts of interest,
              etc.).
            </p>
          </Section>

          <Section title="4. Results & Guarantees">
            <p>
              Review removal outcomes are determined by the review platform
              (Google, Reddit, Glassdoor, Airbnb, Trustpilot), not by Go
              Higher. We cannot guarantee removal of any specific review. Our
              "no removal — no charge" policy is our guarantee: if the platform
              does not remove a review we filed against, no invoice for that
              review is issued.
            </p>
            <p>
              For Monitoring subscriptions, performance metrics such as
              engagement, ranking, or reach depend on many factors outside our
              control and are not guaranteed.
            </p>
          </Section>

          <Section title="5. Payments & Billing">
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Payments are processed by Stripe, Inc. You agree to Stripe's
                terms when providing payment information.
              </li>
              <li>
                Review Removal invoices are issued only after successful
                removal and are due upon receipt.
              </li>
              <li>
                Monitoring subscriptions auto-renew monthly until cancelled by
                you through the client dashboard or by email to
                info@igohigher.com.
              </li>
              <li>
                Except where required by law, subscription payments are
                non-refundable for the current billing cycle. Unused portions
                of a billing cycle are not prorated on cancellation.
              </li>
              <li>
                We accept card and ACH bank transfer. ACH transactions
                typically settle within ~4 business days.
              </li>
            </ul>
          </Section>

          <Section title="6. Client Responsibilities">
            <p>
              You represent that: (a) you are authorized to act on behalf of
              the business you enroll; (b) all information you provide is
              accurate; (c) you will not use the Services for any unlawful
              purpose, including submitting false or defamatory content about
              third parties. You are responsible for maintaining the
              confidentiality of your dashboard login credentials.
            </p>
          </Section>

          <Section title="7. Intellectual Property">
            <p>
              All content on igohigher.com — including copy, design, logos, and
              proprietary methodology — is owned by Go Higher, Inc. and
              protected by copyright and trademark law. Content we create on
              your behalf (social posts, creatives) is licensed to you for
              commercial use on your business channels; the underlying
              templates and methodology remain ours.
            </p>
          </Section>

          <Section title="8. Termination">
            <p>
              We may suspend or terminate access to the Services if you
              breach these Terms, misuse the Services, or provide false
              information. You may terminate a subscription at any time via
              the dashboard or by emailing info@igohigher.com.
            </p>
          </Section>

          <Section title="9. Disclaimers & Limitation of Liability">
            <p>
              The Services are provided "as is" and "as available" without
              warranties of any kind, express or implied. To the maximum extent
              permitted by law, Go Higher, Inc. shall not be liable for any
              indirect, incidental, special, consequential, or punitive
              damages. Our total aggregate liability shall not exceed the
              amount you paid us in the 12 months preceding the claim.
            </p>
          </Section>

          <Section title="10. Governing Law">
            <p>
              These Terms are governed by the laws of the State of Louisiana,
              United States, without regard to conflict-of-law principles. Any
              dispute shall be resolved in the state or federal courts located
              in Orleans Parish, Louisiana.
            </p>
          </Section>

          <Section title="11. Changes to These Terms">
            <p>
              We may update these Terms from time to time. Material changes
              will be notified via email or a notice in the client dashboard.
              Continued use of the Services after changes constitutes
              acceptance.
            </p>
          </Section>

          <Section title="12. Contact">
            <p>
              Questions about these Terms? Email{" "}
              <a
                href="mailto:info@igohigher.com"
                className="text-[#10B981] hover:text-[#0ea774] underline underline-offset-4"
              >
                info@igohigher.com
              </a>{" "}
              or call +92 333 857 5859.
            </p>
          </Section>
        </div>
      </main>

      <footer className="border-t border-white/10 mt-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-6 text-xs text-white/40 flex items-center justify-between">
          <div>© {new Date().getFullYear()} Go Higher, Inc.</div>
          <Link to="/privacy" className="hover:text-white transition-colors">
            Privacy Policy
          </Link>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-bold text-white">{title}</h2>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}
